import { publish } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';
import CartPerformance from './cart-performance';
import fetchConfig from './fetch-config';
import CartNotification from './cart-notification';
import CartDrawer from './cart-drawer';

interface CartResponse {
  status?: boolean;
  errors?: string;
  description?: string;
  message?: string;
  [key: string]: unknown;
}

export default class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cartDrawer = document.querySelector('cart-drawer') as CartDrawer;
    this.cartNotification = document.querySelector('cart-notification') as CartNotification;

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');

    if (document.querySelector('cart-drawer') && !this.submitButton.hasAttribute('data-drawer-upsell')) {
      this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.setAttribute('disabled', 'disabled');
    this.querySelector('#loading-spinner')?.classList.remove('hidden');

    const extendedWarrantyInput = document.querySelector('#ExtendedWarranty') as HTMLInputElement | null;
    const shouldAddExtendedWarranty =
      !!extendedWarrantyInput && extendedWarrantyInput.checked && extendedWarrantyInput.dataset.variantId;

    const ssdCheckedInput = document.querySelector('input[name="ssd-storage-size"]:checked') as HTMLInputElement | null;
    const shouldAddSsdUpsell = !!ssdCheckedInput && !!ssdCheckedInput.value && !!ssdCheckedInput.dataset.variantId;

    const wlanInput = document.querySelector('#upsell-wlan') as HTMLInputElement | null;
    const shouldAddWlan = !!wlanInput && wlanInput.checked && !!wlanInput.dataset.variantId;

    const bluetoothInput = document.querySelector('#upsell-bluetooth') as HTMLInputElement | null;
    const shouldAddBluetooth = !!bluetoothInput && bluetoothInput.checked && !!bluetoothInput.dataset.variantId;

    const linuxInput = document.querySelector('#upsell-linux') as HTMLInputElement | null;
    const shouldAddLinux = !!linuxInput && linuxInput.checked && !!linuxInput.dataset.variantId;

    this.addToCart(
      evt,
      shouldAddExtendedWarranty ? extendedWarrantyInput : null,
      shouldAddSsdUpsell ? ssdCheckedInput : null,
      shouldAddWlan ? wlanInput : null,
      shouldAddBluetooth ? bluetoothInput : null,
      shouldAddLinux ? linuxInput : null,
    );
  }

  private addToCart(
    evt: Event,
    extendedWarrantyInput: HTMLInputElement | null = null,
    ssdUpsellInput: HTMLInputElement | null = null,
    wlanInput: HTMLInputElement | null = null,
    bluetoothInput: HTMLInputElement | null = null,
    linuxInput: HTMLInputElement | null = null,
  ) {
    const properties = this.collectLineItemProperties();
    const items = [
      {
        id: this.variantIdInput.value,
        quantity: 1,
        ...(properties ? { properties } : {}),
      },
    ];

    const prodId = this.getAttribute('data-product-id') || '';
    const linkedTitle =
      this.dataset.productTitle || document.querySelector('product-form')?.getAttribute('data-product-title') || '';

    if (extendedWarrantyInput?.dataset.variantId) {
      items.push({
        id: extendedWarrantyInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (ssdUpsellInput?.dataset.variantId) {
      items.push({
        id: ssdUpsellInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (wlanInput?.dataset.variantId) {
      items.push({
        id: wlanInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (bluetoothInput?.dataset.variantId) {
      items.push({
        id: bluetoothInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (linuxInput?.dataset.variantId) {
      items.push({
        id: linuxInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    let formData = { items };
    if (this.cartDrawer || this.cartNotification) {
      formData = { ...formData, ...this.getCartSectionsData() };
    }

    const config = { ...fetchConfig(), body: JSON.stringify(formData) };
    this.fetchAndHandleCartResponse(`${window.routes.cart_add_url}`, config, this.variantIdInput.value, evt);
  }

  private collectLineItemProperties(): Record<string, string> | undefined {
    const formEl = this.form as HTMLFormElement | null;
    if (!formEl) return undefined;

    const formId = formEl.getAttribute('id') || '';
    const insideFormElements = Array.from(
      formEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[name^="properties["]'),
    );
    const associatedElements = formId
      ? Array.from(
          document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            `[form="${CSS.escape(formId)}"][name^="properties["]`,
          ),
        )
      : [];
    const elements = [...insideFormElements, ...associatedElements];

    const properties: Record<string, string> = {};
    elements.forEach((el) => {
      if (el.disabled) return;

      if (el instanceof HTMLInputElement && (el.type === 'radio' || el.type === 'checkbox')) {
        if (!el.checked) return;
      }

      const match = el.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      const key = match[1];
      const value = (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        properties[key] = value;
      }
    });

    return Object.keys(properties).length ? properties : undefined;
  }

  private getCartSectionsData() {
    const sections = [];

    if (this.cartDrawer) {
      sections.push(...this.cartDrawer.getSectionsToRender().map((section) => section.id));
    }

    if (this.cartNotification) {
      sections.push(...this.cartNotification.getSectionsToRender().map((section) => section.section));
      this.cartNotification.setActiveElement(document.activeElement);
    }

    const data = {
      sections: [...new Set(sections)],
      sections_url: window.location.pathname,
    };

    const bundleInfo = this.getBundleInfo();
    if (bundleInfo) {
      data['properties[_Bundle info]'] = bundleInfo;
    }

    return data;
  }

  private getBundleInfo() {
    const bundleItems = document.querySelectorAll('[id*="bundle-item-"]');
    if (bundleItems.length === 0) return null;

    return Array.from(bundleItems)
      .map((bundle) => bundle.innerHTML?.trim())
      .filter(Boolean)
      .join(', ');
  }

  private fetchAndHandleCartResponse(url: string, config: RequestInit, productVariantId: string | number, evt: Event) {
    fetch(url, config)
      .then((response) => response.json())
      .then((response) => this.handleCartResponse(response, productVariantId, evt))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      })
      .finally(() => this.resetSubmitButton(evt));
  }

  private handleCartResponse(response: CartResponse, productVariantId: string | number, _evt: Event) {
    if (response.status || response.errors) {
      this.handleCartError(response, productVariantId);
    } else if (!this.cartDrawer) {
      window.location = window.routes.cart_url;
    } else {
      this.handleCartSuccess(response, productVariantId);
    }
  }

  private handleCartError(response: CartResponse, productVariantId: string | number) {
    const errorMessage = response.errors || response.description;

    publish(PUB_SUB_EVENTS.cartError, {
      source: 'product-form',
      productVariantId,
      errors: errorMessage,
      message: response.message,
    });

    this.handleErrorMessage(errorMessage);

    const soldOutMessage = this.submitButton.querySelector('#sold-out-message');
    if (soldOutMessage) {
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButtonText.classList.add('hidden');
      soldOutMessage.classList.remove('hidden');
      this.error = true;
    }
  }

  private handleCartSuccess(response: CartResponse, productVariantId: string | number) {
    const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
    if (!this.error) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId,
        cartData: response,
      }).then(() => {
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
      });
    }
    this.error = false;

    const quickAddModal = this.closest('quick-add-modal');
    if (quickAddModal) {
      this.handleQuickAddModal(quickAddModal, response);
    } else {
      CartPerformance.measure('add:paint-updated-sections', () => {
        this.cartNotification.renderContents(response);
      });
    }
  }

  private handleQuickAddModal(quickAddModal: Element, response: CartResponse) {
    document.body.addEventListener(
      'modalClosed',
      () => {
        setTimeout(() => {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cartNotification.renderContents(response);
          });
        });
      },
      { once: true },
    );
    quickAddModal.hide(true);
  }

  private resetSubmitButton(evt: Event) {
    this.submitButton.removeAttribute('disabled');

    if (this.cartDrawer?.classList.contains('is-empty')) {
      this.cartDrawer.classList.remove('is-empty');
    }

    if (!this.error) {
      this.submitButton.removeAttribute('aria-disabled');
    }

    this.querySelector('#loading-spinner')?.classList.add('hidden');
    CartPerformance.measureFromEvent('add:user-action', evt);
  }

  handleErrorMessage(errorMessage?: string | false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('#error-message-wrapper');

    if (!this.errorMessageWrapper) return;

    this.errorMessageWrapper.classList.add('hidden');

    if (this.hideErrors || !errorMessage) return;

    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('#error-message');
    if (this.errorMessage) {
      this.errorMessage.textContent = errorMessage;
      this.errorMessageWrapper.classList.add('flex');
      this.errorMessageWrapper.classList.remove('hidden');
    }
  }

  toggleSubmitButton(disable: boolean = true, text?: string): void {
    if (!this.submitButton || !this.submitButtonText) return;

    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) {
        this.submitButtonText.textContent = text;
      }
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent =
        (window as Window & { variantStrings?: { addToCart: string } }).variantStrings?.addToCart || 'Add to cart';
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
